import '@tempots/beatui/prosemirror.css'
import { RequireAuth } from '@/components/require-auth'
import {
  Center,
  Control,
  Notification,
  BaseListControl,
  Switch,
  TextInput,
  useForm,
  ObjectController,
  Icon,
  PageDropZone,
  Tooltip,
} from '@tempots/beatui'
import {
  attr,
  computedOf,
  dataAttr,
  Fragment,
  html,
  on,
  prop,
  Signal,
  When,
} from '@tempots/dom'
import { z } from 'zod'
import { Anchor, HTMLTitle, Query } from '@tempots/ui'
import { UserService } from '@/services/user-service'
import { type RedactionPreferences } from '@rh/shared/stores/auth-store'
import { authState } from '@/stores/auth-store'
import { ResumeState } from '@/models/resume-state'
import {
  ResumeExpiredError,
  ResumePDFPreview,
} from '@/components/resume-pdf-preview'
import { PdfService } from '@/services/pdf-service'
import { Notify } from '@rh/shared/utils/notify'
import { Track } from '@/services/track'
import { RESUME_UPLOAD_ACCEPT } from '@rh/shared/constants/resume-content-types'
import { AppLayout } from '@/components/app-layout'
import { CustomizeNudge } from '@/components/customize-nudge'
import {
  customizeNudgeDismissedKey,
  isUnconfiguredDesign,
} from '@/pages/customize/unconfigured-design'
import { ApiError } from '@rh/shared/services/auth-service'
import { EphimeralService } from '@/utils/ephimeral'
import {
  AgencyService,
  PLAN_LABELS,
  type PlanType,
} from '@/services/agency-service'
import { WriteUpPanel } from '@/components/writeup-panel'
import {
  DropCatchGutter,
  ResumeDropOverlay,
} from '@/components/resume-drop-overlay'

const schema = z.object({
  phoneNumbers: z.boolean(),
  socialLinks: z.boolean(),
  emails: z.boolean(),
  urls: z.boolean(),
  // NullableTextInput's clear button sets null; the server treats null as ''
  name: z.string().nullable(),
  keywords: z.array(z.string()),
  caseSensitive: z.boolean(),
  writeup: z.string().optional(),
})

type AppLayoutSchema = z.infer<typeof schema>

type OptionsPanelOptions = {
  controller: ObjectController<AppLayoutSchema>
  /** Plan gate: false swaps the panel for the solo-user layout. */
  canRedact: Signal<boolean>
  agencyId: Signal<string>
  setResumeFile: (file: File | null) => void
  autodownload: Signal<boolean>
  setAutodownload: (v: boolean) => void
}

function OptionsPanel({
  controller,
  canRedact,
  agencyId,
  setResumeFile,
  autodownload,
  setAutodownload,
}: OptionsPanelOptions) {
  const phoneNumbers = controller.field('phoneNumbers')
  const emails = controller.field('emails')
  const urls = controller.field('urls')
  const socialLinks = controller.field('socialLinks')
  const keywords = controller.field('keywords').array()
  const otherText = prop('')
  // One layout for every plan: upload target, customize note, a compact
  // redaction section (or a quiet Pro pointer for plans without it), and
  // the candidate write-up. No tabs.
  return html.div(
    attr.class('overflow-y-auto h-full p-4 flex flex-col gap-4'),

    // The page-wide drop zone lives here now (the top bar lost its button):
    // the visible box is the click target, and the auto-download toggle
    // floats over its corner as a sibling of the button so toggling never
    // opens the file picker.
    PageDropZone({
      accept: RESUME_UPLOAD_ACCEPT,
      onDragContent: ResumeDropOverlay,
      onChange: files => setResumeFile(files[0] ?? null),
      onInvalidSelection: () => Notify.warn('Invalid resume file'),
      content: ({ selectFiles }) =>
        html.div(
          attr.class('relative flex-none'),
          html.button(
            attr.type('button'),
            attr.id('candidate-resume-dropzone'),
            on.click(selectFiles),
            attr.class(
              'block w-full border-2 border-dashed border-gray-300 rounded-xl bg-[#fffaf8] hover:border-teal-700 hover:bg-gray-50 transition-colors cursor-pointer text-center px-6 pt-8 pb-12'
            ),
            html.div(
              attr.class('text-teal-700 font-semibold'),
              'Drop a resume here'
            ),
            html.div(
              attr.class('text-sm text-gray-500 mt-1'),
              dataAttr('testid', 'candidate-resume-dropzone-text'),
              'or click to choose a file'
            )
          ),
          html.div(
            attr.class(
              'absolute bottom-1 right-2 flex gap-1 items-center flex-nowrap px-1 py-1'
            ),
            Switch({
              size: 'xs',
              value: autodownload,
              onChange: setAutodownload,
            }),
            html.span(
              attr.class('text-xs text-gray-700 whitespace-nowrap'),
              'Auto download'
            ),
            html.button(
              attr.type('button'),
              attr.class('flex items-center cursor-pointer'),
              Icon({
                icon: 'line-md:question-circle',
                color: 'gray',
                size: 'sm',
              }),
              Tooltip({
                showOn: 'click',
                placement: 'top',
                content: html.div(
                  attr.class('text-xs flex flex-col gap-2 leading-4'),
                  html.div(
                    'By selecting auto-download, as soon as you drop a resume here, it will be formatted and automatically downloaded'
                  ),
                  html.div(
                    'This is perfect for when your headers and footers are setup the way you like them, and you just want to brand resumes quickly.'
                  )
                ),
              })
            )
          )
        ),
    }),
    html.div(attr.class('font-bold text-sm -mb-3'), 'Redaction'),
    When(
      canRedact.map(allowed => !allowed),
      () =>
        html.p(
          attr.class('text-xs text-gray-400 pl-3'),
          'Redact contact details, names, or keywords from resumes — ',
          Anchor(
            { href: agencyId.map(id => `/${id}/billing`) },
            attr.class('text-teal-700 hover:underline font-medium'),
            'upgrade to Pro'
          ),
          ' to unlock.'
        )
    ),
    When(canRedact, () =>
      html.div(
        attr.class('flex flex-col gap-1.5 pl-3'),
        html.div(
          attr.class('grid grid-cols-2 gap-x-4 gap-y-0.5'),
          ...(
            [
              [phoneNumbers, 'phoneNumbers', 'Phone Numbers'],
              [urls, 'urls', 'URLs'],
              [emails, 'emails', 'Email Addresses'],
              [socialLinks, 'socialLinks', 'Social Links'],
            ] as const
          ).map(([field, trackName, label]) => {
            const toggle = (v: boolean) => {
              Track.fieldInput({
                section: 'main',
                field_name: trackName,
                previous_value: String(field.signal.value),
                new_value: String(v),
              })
              field.change(v)
            }
            return html.div(
              attr.class('flex items-center gap-2'),
              Switch({
                size: 'xs',
                value: field.signal,
                onChange: toggle,
              }),
              html.span(
                attr.class(
                  'text-sm font-semibold text-gray-700 cursor-pointer select-none'
                ),
                on.click(() => toggle(!field.signal.value)),
                label
              )
            )
          })
        ),
        html.div(
          attr.class(
            'grid grid-cols-[auto_1fr] items-center gap-x-2 gap-y-1 max-w-xs'
          ),
          html.span(
            attr.class('text-sm font-semibold text-gray-700 whitespace-nowrap'),
            'Other Text'
          ),
          html.div(
            TextInput({
              size: 'sm',
              placeholder: 'Add text to redact…',
              value: otherText,
              onChange: v => {
                otherText.set(v)
                if (v.trim() === '') return
                Track.fieldInput({
                  section: 'main',
                  field_name: 'keywords',
                  previous_value: '',
                  new_value: v.trim(),
                })
                keywords.push(v.trim())
                otherText.set('')
              },
            })
          ),
          html.div(
            attr.class('col-start-2'),
            BaseListControl({
              controller: keywords,
              showMove: false,
              element: ({ item }) =>
                Control(TextInput, {
                  size: 'sm',
                  controller: item,
                  onChange: v => {
                    Track.fieldInput({
                      section: 'main',
                      field_name: 'keywords',
                      previous_value: item.signal.value,
                      new_value: v,
                    })
                  },
                }),
            })
          )
        )
      )
    ),
    html.div(
      attr.class('flex flex-col flex-grow min-h-[240px]'),
      html.div(attr.class('font-bold text-sm mb-2'), 'Candidate Write-Up'),
      html.div(
        attr.class('flex-grow min-h-0'),
        WriteUpPanel({ controller: controller.field('writeup') })
      )
    )
  )
}

export function WorkspacePage({
  agencyId,
  resumeFile,
  setResumeFile,
}: {
  agencyId: Signal<string>
  resumeFile: File | null
  setResumeFile: (file: File | null) => void
}) {
  const agency = agencyId.mapAsync(async id => {
    try {
      return await AgencyService.getAgency(id)
    } catch {
      return undefined
    }
  }, undefined)
  const agencyName = agency.map(a => a?.name)

  // Plan-derived state: redaction gate (default true while loading so the
  // lock never flashes for entitled agencies) and the current plan (for
  // the skipped-paid-intent reminder chip).
  const planState = prop({
    canRedact: true,
    plan: null as string | null,
    resumesPerMonth: null as number | null,
    resumesUsed: null as number | null,
    trialEndsAt: null as string | null,
    trialExpired: false,
  })
  // Refreshed on load and after every processing attempt so the free-tier
  // meter tracks reality
  const refreshPlanState = async () => {
    try {
      const info = await AgencyService.getPlanInfo(agencyId.value)
      planState.set({
        canRedact: info.availableFeatures.includes('redaction'),
        plan: info.plan as string | null,
        resumesPerMonth: info.resumesPerMonth ?? null,
        resumesUsed: info.resumesUsed ?? null,
        trialEndsAt: info.trialEndsAt ?? null,
        trialExpired: info.trialExpired === true,
      })
    } catch {
      // keep previous state
    }
  }
  agencyId.on(() => void refreshPlanState())
  // The server buckets usage by UTC calendar month
  const quotaResetLabel = (() => {
    const now = new Date()
    return new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1)
    ).toLocaleDateString(undefined, {
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    })
  })()
  const canRedact = planState.map(s => s.canRedact)
  const storedPlanIntent = EphimeralService.retrieve<{
    type: 'planIntent'
    plan?: string
  }>('planIntent')
  const planIntentDismissed = prop(false)

  // Trial-ended banner: dismissable per agency, like the customize nudge
  const trialEndedDismissedKey = (id: string) => `trialEndedDismissed:${id}`
  const trialEndedDismissed = prop(false)
  const dismissTrialEnded = () => {
    localStorage.setItem(trialEndedDismissedKey(agencyId.value), '1')
    trialEndedDismissed.set(true)
  }
  const trialDaysLeft = (iso: string) =>
    Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000))

  // First-time nudge: while the design is still the onboarding seed, point
  // at the Customize link. Retired on dismissal or any /customize visit.
  const nudgeDismissed = prop(false)
  const showNudge = computedOf(
    agency,
    nudgeDismissed
  )(
    (a, dismissed) =>
      !dismissed &&
      a !== undefined &&
      isUnconfiguredDesign(a.preferences ?? {}) &&
      localStorage.getItem(customizeNudgeDismissedKey(a.id)) === null
  )
  const dismissNudge = () => {
    localStorage.setItem(customizeNudgeDismissedKey(agencyId.value), '1')
    nudgeDismissed.set(true)
  }
  return RequireAuth({}, () => {
    return Query<string, RedactionPreferences | null>({
      request: agencyId,
      load: ({ request: currentAgencyId }) =>
        UserService.getRedactionPreferences(currentAgencyId).then(response => {
          return response.redactionPreferences
        }),
      success: ({ value: redactionPreferences }) => {
        const { tick, resume } = ResumeState.makeResumeState({
          resumeFile,
          setResumeFile,
        })
        // Auto-download must survive navigating away (e.g. to /customize)
        // and back; it's a device-level convenience, so localStorage per
        // agency — like the banner dismissals — not the server profile.
        const autodownloadKey = (id: string) => `autodownload:${id}`
        const autodownload = prop(
          localStorage.getItem(autodownloadKey(agencyId.value)) === '1'
        )
        const setAutodownload = (v: boolean) => {
          if (v) localStorage.setItem(autodownloadKey(agencyId.value), '1')
          else localStorage.removeItem(autodownloadKey(agencyId.value))
          autodownload.set(v)
        }
        const initial = redactionPreferences.value ?? {
          phoneNumbers: false,
          socialLinks: false,
          emails: false,
          urls: false,
          keywords: [],
          caseSensitive: false,
        }
        const { controller } = useForm<AppLayoutSchema>({
          schema,
          initialValue: {
            phoneNumbers: initial.phoneNumbers,
            socialLinks: initial.socialLinks,
            emails: initial.emails,
            urls: initial.urls,
            name: '',
            keywords: initial.keywords,
            caseSensitive: initial.caseSensitive,
            writeup: '',
          } as AppLayoutSchema,
        })

        const savePreferencesToBackend = async (
          prefs: RedactionPreferences
        ) => {
          try {
            return await UserService.updateRedactionPreferences(
              agencyId.value,
              prefs
            )
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
          } catch (_error) {
            Notify.error('Failed to save redaction preferences')
          }
        }

        // Save to backend on controller changes (skip initial value). Only
        // persist when the redaction fields actually changed — the form
        // signal also fires for every write-up keystroke.
        const redactionOf = (v: AppLayoutSchema): RedactionPreferences => ({
          phoneNumbers: v.phoneNumbers,
          socialLinks: v.socialLinks,
          emails: v.emails,
          urls: v.urls,
          keywords: v.keywords,
          caseSensitive: v.caseSensitive,
        })
        let isFirstEmission = true
        let lastSavedRedaction = JSON.stringify(
          redactionOf(controller.signal.value)
        )
        controller.signal.on(async v => {
          if (isFirstEmission) {
            isFirstEmission = false
            return
          }
          const redaction = redactionOf(v)
          const serialized = JSON.stringify(redaction)
          if (serialized !== lastSavedRedaction) {
            lastSavedRedaction = serialized
            await savePreferencesToBackend(redaction)
          }
          tick.tick()
        })

        return Fragment(
          HTMLTitle('Workspace - ResumeHeaders'),
          When(showNudge, () => CustomizeNudge({ onDismiss: dismissNudge })),
          AppLayout({
            agencyId,
            content: html.div(
              attr.class('h-full flex flex-col overflow-hidden'),
              // Sits directly under the top nav so it reads as a pointer
              // at the Customize menu item above it
              html.p(
                attr.class(
                  'flex-none text-xs sm:text-sm text-teal-700 text-center px-4 mt-3'
                ),
                'Go to `Customize` to format your headers, footers, watermark and more'
              ),
              // Invited users skip onboarding, so nothing captured their
              // name; nudge until the profile has one (reactive — saves
              // on /profile update the auth store and dismiss this).
              When(
                authState.$.user.map(
                  u => u != null && (u.fullName ?? '').trim() === ''
                ),
                () =>
                  html.div(
                    attr.class(
                      'flex-none mx-4 mt-3 bg-teal-50 border border-teal-200 rounded-lg px-3 py-2 text-sm text-teal-900'
                    ),
                    'Welcome! Add your name so your team knows who you are — ',
                    Anchor(
                      { href: agencyId.map(id => `/${id}/profile`) },
                      attr.class('font-semibold hover:underline'),
                      'complete your profile'
                    ),
                    '.'
                  )
              ),
              // Trial countdown: quiet chip while the 14-day trial runs
              When(
                planState.map(
                  state => state.plan === 'trial' && state.trialEndsAt != null
                ),
                () =>
                  html.div(
                    attr.class(
                      'flex-none mx-4 mt-3 text-xs text-center text-gray-500'
                    ),
                    html.span(
                      planState.map(state => {
                        const days = trialDaysLeft(state.trialEndsAt ?? '')
                        return `Free trial — ${days} day${days === 1 ? '' : 's'} left · `
                      })
                    ),
                    Anchor(
                      { href: agencyId.map(id => `/${id}/billing`) },
                      attr.class('text-teal-700 hover:underline font-semibold'),
                      'see plans'
                    )
                  )
              ),
              // Trial ended: the agency lapsed to the free tier
              When(
                computedOf(
                  planState,
                  trialEndedDismissed
                )(
                  (state, dismissed) =>
                    !dismissed &&
                    state.trialExpired &&
                    state.plan === 'free' &&
                    localStorage.getItem(
                      trialEndedDismissedKey(agencyId.value)
                    ) === null
                ),
                () =>
                  html.div(
                    attr.class(
                      'flex-none mx-4 mt-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-800 flex items-center gap-2'
                    ),
                    html.span(
                      'Your trial ended — you’re on the free tier (5 resumes/month). '
                    ),
                    Anchor(
                      { href: agencyId.map(id => `/${id}/billing`) },
                      attr.class('text-teal-700 font-semibold hover:underline'),
                      'Pick a plan'
                    ),
                    html.button(
                      attr.type('button'),
                      attr.class(
                        'ml-auto text-amber-400 hover:text-amber-600 cursor-pointer'
                      ),
                      attr.title('Dismiss'),
                      on.click(dismissTrialEnded),
                      '×'
                    )
                  )
              ),
              // Skipped-paid-intent reminder: they clicked a paid pricing
              // card but skipped checkout; keep a quiet path back until
              // they subscribe or dismiss.
              When(
                computedOf(
                  planState,
                  planIntentDismissed
                )(
                  (state, dismissed) =>
                    !dismissed &&
                    storedPlanIntent?.plan != null &&
                    (state.plan === 'free' || state.plan === 'trial')
                ),
                () =>
                  html.div(
                    attr.class(
                      'flex-none mx-4 mt-3 bg-gray-100 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 flex items-center gap-2'
                    ),
                    html.span(
                      `You picked ${PLAN_LABELS[storedPlanIntent!.plan as PlanType] ?? storedPlanIntent!.plan} — `
                    ),
                    Anchor(
                      {
                        href: agencyId.map(
                          id =>
                            `/${id}/billing?intent=${storedPlanIntent!.plan}`
                        ),
                      },
                      attr.class('text-teal-700 font-semibold hover:underline'),
                      'finish setting it up'
                    ),
                    html.button(
                      attr.type('button'),
                      attr.class(
                        'ml-auto text-gray-400 hover:text-gray-600 cursor-pointer'
                      ),
                      attr.title('Dismiss'),
                      on.click(() => {
                        EphimeralService.remove('planIntent')
                        planIntentDismissed.set(true)
                      }),
                      '×'
                    )
                  )
              ),
              // Free-tier resume meter (only plans with a monthly cap)
              When(
                planState.map(state => state.resumesPerMonth != null),
                () =>
                  html.div(
                    attr.class('flex-none mx-4 mt-3 text-xs text-center'),
                    When(
                      planState.map(
                        state =>
                          (state.resumesUsed ?? 0) >=
                          (state.resumesPerMonth ?? Infinity)
                      ),
                      () =>
                        html.div(
                          attr.class('text-amber-700 font-semibold'),
                          html.div(
                            planState.map(
                              state =>
                                `All ${state.resumesPerMonth} free resumes used this month (resets ${quotaResetLabel})`
                            )
                          ),
                          html.div(
                            Anchor(
                              { href: agencyId.map(id => `/${id}/billing`) },
                              attr.class(
                                'text-teal-700 hover:underline font-semibold'
                              ),
                              'upgrade for unlimited'
                            )
                          )
                        ),
                      () =>
                        html.span(
                          attr.class('text-gray-500'),
                          planState.map(
                            state =>
                              `${state.resumesUsed ?? 0} of ${state.resumesPerMonth} free resumes used this month`
                          )
                        )
                    )
                  )
              ),
              OptionsPanel({
                controller,
                canRedact,
                agencyId,
                // the workspace's own uploader (the app-level setResumeFile
                // only navigates when already on this page)
                setResumeFile: file =>
                  ResumeState.upload(file, v => resume.set(v), {
                    section: 'workspace',
                  }),
                autodownload,
                setAutodownload,
              })
            ),
            // The gutter keeps the preview iframe off the window's right
            // edge so drags entering from that side hit parent-document
            // territory first and trigger the drop overlay.
            rightPane: html.div(
              attr.class('h-full flex overflow-hidden'),
              html.div(
                attr.class('flex-1 min-w-0'),
                ResumePDFPreview({
                  agencyName,
                  tick,
                  state: resume,
                  // Audit trail: every download (button or auto-download) of a
                  // processed resume is recorded as a `resume.download` event.
                  onDownload: () => {
                    const state = resume.value
                    if (state.type !== 'uploaded') return
                    PdfService.trackResumeDownload(
                      state.hash,
                      agencyId.value,
                      state.filename
                    )
                  },
                  notAsked: () =>
                    html.div(
                      attr.class(
                        'h-full overflow-hidden bg-gray-100 border-t border-gray-200 md:border-t-0 md:border-l'
                      ),
                      Center(
                        {},
                        html.div(
                          attr.class(
                            'max-w-96 text-center flex flex-col gap-8 lg:gap-16 px-4'
                          ),
                          html.h2(
                            attr.class(
                              'text-gray-800 text-sm sm:text-base md:text-md font-semibold'
                            ),
                            html.span(
                              attr.class('hidden lg:inline'),
                              'Drop a resume anywhere on this page — or use the box on the left — to see the processed output here'
                            ),
                            html.span(
                              attr.class('lg:hidden'),
                              'Upload a resume to see the processed output'
                            )
                          ),
                          html.p(
                            attr.class(
                              'text-gray-600 text-sm sm:text-base md:text-md'
                            ),
                            'If you are running a team, invite members under "Agency" in the top menu.'
                          )
                        )
                      )
                    ),
                  getUrl: async (hash: string) => {
                    const { writeup, ...redactionOptions } =
                      controller.signal.value
                    const trimmedWriteup =
                      typeof writeup === 'string' && writeup.trim().length > 0
                        ? writeup
                        : undefined
                    try {
                      const response =
                        await PdfService.processResumeWithDetails(
                          hash,
                          agencyId.value,
                          {
                            redactionOptions,
                            writeup: trimmedWriteup,
                          }
                        )
                      return {
                        url: response.url,
                        missingFonts: response.missingFonts,
                      }
                    } catch (error) {
                      if (
                        error instanceof ApiError &&
                        (error.status === 410 || error.status === 409)
                      ) {
                        EphimeralService.remove('latestResume')
                        resume.set(ResumeState.notAsked)
                        throw new ResumeExpiredError()
                      }
                      if (
                        error instanceof ApiError &&
                        error.code === 'RESUME_LIMIT_EXCEEDED'
                      ) {
                        // marker consumed by renderFailure below (cause
                        // attached for the preserve-caught-error rule; the
                        // TS lib target predates ErrorOptions)
                        const limitError = new Error('RESUME_LIMIT_EXCEEDED')
                        ;(limitError as Error & { cause?: unknown }).cause =
                          error
                        throw limitError
                      }
                      throw error
                    } finally {
                      void refreshPlanState()
                    }
                  },
                  renderFailure: error =>
                    When(
                      error.map(e => e === 'RESUME_LIMIT_EXCEEDED'),
                      () =>
                        Center(
                          {},
                          html.div(
                            attr.class(
                              'max-w-md bg-white border-2 border-teal-600 rounded-xl shadow-sm p-6 text-center flex flex-col gap-3'
                            ),
                            html.div(
                              attr.class('text-lg font-bold text-gray-800'),
                              planState.map((state): string =>
                                state.trialExpired
                                  ? 'You’ve used this month’s 5 free resumes 🎉'
                                  : 'You’ve branded 5 resumes this month 🎉'
                              )
                            ),
                            html.div(
                              attr.class('text-sm text-gray-600'),
                              // Post-trial users had unlimited (and redaction)
                              // two weeks ago — acknowledge that instead of
                              // pretending the 5/month cap is all they know.
                              planState.map((state): string =>
                                state.trialExpired
                                  ? 'Your trial has ended, and the free tier covers 5 resumes a month — clearly ResumeHeaders is still earning its keep. Pick up where your trial left off: Standard for unlimited resumes, or Pro to bring back redaction too.'
                                  : 'Looks like ResumeHeaders is earning its keep — that’s the Free plan’s monthly limit. Upgrade to Standard for unlimited resumes, or Pro to add redaction too.'
                              )
                            ),
                            html.div(
                              Anchor(
                                {
                                  href: agencyId.map(id => `/${id}/billing`),
                                },
                                attr.class(
                                  'inline-block px-4 py-2 rounded-md bg-teal-700 text-white text-sm font-semibold hover:bg-teal-800'
                                ),
                                'See plans'
                              )
                            ),
                            html.div(
                              attr.class('text-xs text-gray-400'),
                              `Your previously processed resumes keep working — the limit only applies to new ones. Your count resets on ${quotaResetLabel}.`
                            )
                          )
                        ),
                      () =>
                        Center(
                          {},
                          html.div(
                            attr.class('p-8'),
                            Notification(
                              {
                                color: 'danger',
                                title: 'Failed to process resume',
                              },
                              error
                            )
                          )
                        )
                    ),
                  // Suppress the auto-download for the resume restored on
                  // mount: re-entering the page re-renders (and would
                  // re-download) the previous session's resume. Fresh
                  // uploads still fire.
                  autodownload: computedOf(
                    autodownload,
                    resume
                  )(
                    (auto, r) =>
                      auto && !(r.type === 'uploaded' && r.restored === true)
                  ),
                })
              ),
              DropCatchGutter()
            ),
          })
        )
      },
    })
  })
}
